import React, { useState, useEffect } from "react";
import {
  FaChevronDown,
  FaChevronUp,
  FaSave,
  FaCalendarAlt,
  FaTrash,
} from "react-icons/fa";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useParams } from "react-router-dom";
import { registerLocale } from "react-datepicker";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ptBR from "date-fns/locale/pt-BR";
import { useAproveitamento } from "../context/AproveitamentoContext";
import { useUser } from "../context/UserContext";
import "../css/Aproveitamentos.css";
import SelectBoxResponsavelPesquisa from "./SelectBoxResponsavelPesquisa";
import SelectBoxResponsavelCadastro from "./SelectBoxResponsavelCadastro";
import BackButton from "../components/BackButton";
import GenericModal from "../components/GenericModal";

import { useLoading } from "../context/LoadingContext";

registerLocale("pt-BR", ptBR);

const Aproveitamentos = () => {
  const { buscarAproveitamentos, salvarAproveitamento, deleteAproveitamento, today } =
    useAproveitamento();
  const { id } = useParams();
  const { userDecoded } = useUser();
  const { setIsLoading } = useLoading();

  const _decoded = userDecoded();
  const _isDev = _decoded?.perfis?.includes("DESENVOLVEDOR");
  const _defaultResponsavel = !_isDev && _decoded?.userId ? _decoded.userId : "";

  const [selectedMonth, setSelectedMonth] = useState(today);
  const [aproveitamentos, setAproveitamentos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [novoAproveitamento, setNovoAproveitamento] = useState(false);
  const [responsavelSelecionadoPesquisa, setResponsavelSelecionadoPesquisa] =
    useState(_defaultResponsavel);
  const [responsavelSelecionadoCadastro, setResponsavelSelecionadoCadastro] =
    useState(_defaultResponsavel);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [aproveitamentoToDelete, setAproveitamentoToDelete] = useState(null);

  const [formData, setFormData] = useState({
    data: today.toISOString().split("T")[0],
    nomeIngrediente: "",
    tipoAproveitamento: "",
    bruto: "",
    limpo: "",
    skin: 0,
    descarte: "",
  });

  const reloadAproveitamentos = async (
    date,
    responsavelSelecionadoPesquisa
  ) => {
    const toastId = toast.loading("Buscando aproveitamentos...");
    setIsLoading(true);
    const mes = date.getMonth() + 1;
    const ano = date.getFullYear();
    try {
      const dados = await buscarAproveitamentos(
        mes,
        ano,
        responsavelSelecionadoPesquisa,
        id
      );
      setAproveitamentos(dados);
      toast.update(toastId, {
        render: "Busca concluída!",
        type: "success",
        isLoading: false,
        autoClose: 2000,
      });
    } catch (error) {
      toast.update(toastId, {
        render: "Erro ao carregar aproveitamentos.",
        type: "error",
        isLoading: false,
        autoClose: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    reloadAproveitamentos(selectedMonth, responsavelSelecionadoPesquisa);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.nomeIngrediente || !formData.tipoAproveitamento) {
      toast.error("Preencha todos os campos obrigatórios.");
      return;
    }

    const toastId = toast.loading("Salvando aproveitamento...");
    setIsLoading(true);
    try {
      const dto = {
        nomeIngrediente: formData.nomeIngrediente,
        tipoAproveitamento: formData.tipoAproveitamento,
        data: formData.data,
        bruto: parseFloat(formData.bruto),
        limpo: parseFloat(formData.limpo),
        skin: parseFloat(formData.skin),
        descarte: parseFloat(formData.descarte),
        responsavelId: parseInt(responsavelSelecionadoCadastro),
      };

      await salvarAproveitamento(dto, id);
      await reloadAproveitamentos(
        selectedMonth,
        responsavelSelecionadoCadastro
      );
      setNovoAproveitamento(false);
      setFormData({
        data: today.toISOString().split("T")[0],
        nomeIngrediente: "",
        tipoAproveitamento: "",
        bruto: "",
        limpo: "",
        skin: 0,
        descarte: "",
      });

      toast.update(toastId, {
        render: "Aproveitamento salvo com sucesso!",
        type: "success",
        isLoading: false,
        autoClose: 2000,
      });
    } catch (error) {
      console.error("Erro ao salvar aproveitamento:", error);
      toast.update(toastId, {
        render: "Erro ao salvar aproveitamento",
        type: "error",
        isLoading: false,
        autoClose: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteClick = (aproveitamento) => {
    setAproveitamentoToDelete(aproveitamento);
    setShowDeleteConfirmModal(true);
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirmModal(false);
    setAproveitamentoToDelete(null);
  };

  const handleConfirmDelete = async () => {
    const toastId = toast.loading("Excluindo aproveitamento...");
    setIsLoading(true);
    try {
      await deleteAproveitamento(aproveitamentoToDelete.id, id);
      toast.update(toastId, {
        render: "Aproveitamento excluído com sucesso!",
        type: "success",
        isLoading: false,
        autoClose: 2000,
      });
      reloadAproveitamentos(selectedMonth, responsavelSelecionadoPesquisa);
      handleCancelDelete();
    } catch (error) {
      console.error("Error deleting aproveitamento:", error);
      toast.update(toastId, {
        render: "Erro ao excluir aproveitamento.",
        type: "error",
        isLoading: false,
        autoClose: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formatarData = (dataString) => {
    const dataComTimezone = `${dataString}T00:00:00-03:00`;
    const data = new Date(dataComTimezone);

    return data.toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const perfisComAcesso = ["DESENVOLVEDOR", "ADMIN", "GERENTE"];
  const decoded = userDecoded();
  const temAcesso = decoded?.perfis?.some((perfil) =>
    perfisComAcesso.includes(perfil)
  );

  return (
    <div className="relatorio-container">
      <BackButton inline />
      <h1>Listagem de Aproveitamentos</h1>

      <div className="filtro-container">
        <div className="periodo-selecionado">
          <span>Período: </span>
        </div>
        <div className="filtro-data-group">
          <div className="filtro-data">
            <FaCalendarAlt className="filtro-icon" />
            <DatePicker
              selected={selectedMonth}
              onChange={(date) => setSelectedMonth(date)}
              locale="pt-BR"
              dateFormat="MM/yyyy"
              showMonthYearPicker
              className="date-picker-input"
              placeholderText="Selecione o mês"
            />
          </div>
        </div>
        <div>
          <SelectBoxResponsavelPesquisa
            value={responsavelSelecionadoPesquisa}
            onChange={setResponsavelSelecionadoPesquisa}
          />
        </div>
        <div className="botoes-superiores">
          <button
            className="btn-adicionar"
            onClick={() =>
              reloadAproveitamentos(
                selectedMonth,
                responsavelSelecionadoPesquisa
              )
            }
            disabled={loading}
          >
            Buscar
          </button>
        </div>
      </div>
      {temAcesso && (
        <div className="botoes-superiores">
          <button
            className="btn-novo"
            onClick={() => setNovoAproveitamento(!novoAproveitamento)}
            disabled={loading}
          >
            {novoAproveitamento ? "Cancelar" : "Novo aproveitamento"}
          </button>
        </div>
      )}
      {temAcesso && novoAproveitamento && (
        <form className="form-novo" onSubmit={handleSubmit}>
          <h2>Novo Aproveitamento</h2>
          <div className="form-group">
            <label>
              Nome do Ingrediente:
              <input
                type="text"
                name="nomeIngrediente"
                value={formData.nomeIngrediente}
                onChange={handleInputChange}
                required
                placeholder="Digite o nome do ingrediente"
              />
            </label>
          </div>

          <div className="form-group">
            <label>
              Tipo de Aproveitamento:
              <select
                name="tipoAproveitamento"
                value={formData.tipoAproveitamento}
                onChange={handleInputChange}
                required
              >
                <option value="">Selecione...</option>
                <option value="Diário">Diário</option>
                <option value="Compra">Compra</option>
              </select>
            </label>

            <label>
              Bruto (KG):
              <input
                type="number"
                name="bruto"
                value={formData.bruto}
                onChange={handleInputChange}
                required
              />
            </label>

            <label>
              Limpo (KG):
              <input
                type="number"
                name="limpo"
                value={formData.limpo}
                onChange={handleInputChange}
                required
              />
            </label>

            <label>
              Skin (KG):
              <input
                type="number"
                name="skin"
                value={formData.skin}
                onChange={handleInputChange}
              />
            </label>

            <label>
              Descarte (KG):
              <input
                type="number"
                name="descarte"
                value={formData.descarte}
                onChange={handleInputChange}
              />
            </label>
          </div>
          <div className="form-group">
            <label>
              Data:
              <input
                type="date"
                name="data"
                value={formData.data}
                onChange={handleInputChange}
                required
              />
            </label>
          </div>
          <div className="detalhes-form">
            <div>
              <SelectBoxResponsavelCadastro
                value={responsavelSelecionadoCadastro}
                onChange={setResponsavelSelecionadoCadastro}
              />
            </div>
          </div>

          <button type="submit" className="btn-salvar" disabled={loading}>
            <FaSave /> {loading ? "Salvando..." : "Salvar"}
          </button>
        </form>
      )}

      {loading ? (
        <div className="loading-spinner">Carregando...</div>
      ) : (
        <div className="lista-aproveitamentos">
          {aproveitamentos.length > 0 ? (
            aproveitamentos.map((item) => (
              <div
                key={item.id}
                className={`aproveitamento-card ${
                  expandedId === item.id ? "expanded" : ""
                }`}
              >
                <div
                  className="card-header"
                  onClick={() => toggleExpand(item.id)}
                >
                  <span>
                    {item.nomeIngrediente} - {formatarData(item.data)}
                  </span>
                  <div className="edit-delete-icons">
                    
                  <div
                    className="delete-icon"
                    onClick={() => handleDeleteClick(item)}
                  >
                    <FaTrash />
                  </div>
                    {expandedId === item.id ? <FaChevronUp /> : <FaChevronDown />}
                  </div>
                </div>

                {expandedId === item.id && (
                  <div className="card-detalhes">
                    <p>
                      <strong>Tipo:</strong> {item.tipoAproveitamento}
                    </p>
                    <p>
                      <strong>Bruto:</strong> {item.bruto} KG
                    </p>
                    <p>
                      <strong>Limpo:</strong> {item.limpo} KG
                    </p>
                    <p>
                      <strong>Skin:</strong> {item.skin} KG
                    </p>
                    <p>
                      <strong>Descarte:</strong>{" "}
                      {item.descarte != null ? item.descarte : 0.0} KG
                    </p>
                    <p>
                      <strong>Responsável:</strong> {item.responsavelNome}
                    </p>
                  </div>
                )}
              </div>
            ))
          ) : (
            <p className="sem-resultados">
              Nenhum aproveitamento encontrado para o período selecionado
            </p>
          )}
        </div>
      )}

      <GenericModal
        isOpen={showDeleteConfirmModal}
        onClose={handleCancelDelete}
      >
        <div className="modal-content">
          <h2>Confirmar Exclusão</h2>
          <p>
            Tem certeza que deseja excluir o aproveitamento de "
            {aproveitamentoToDelete?.nomeIngrediente}"?
          </p>
          <div className="modal-actions">
            <button onClick={handleCancelDelete} className="btn-secundario">
              Cancelar
            </button>
            <button
              onClick={handleConfirmDelete}
              className="btn-primario delete-button"
            >
              Excluir
            </button>
          </div>
        </div>
      </GenericModal>

      <ToastContainer position="top-right" autoClose={3000} theme="colored" />
    </div>
  );
};

export default Aproveitamentos;

